import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Box,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppDispatch, useAppSelector } from '../../../store/hooks.ts';
import {
  addDoraControl,
  removeDoraControl,
  selectDoraControls,
  setDoraControls,
  updateDoraControl as updateDoraControlState,
} from '../store/control/controlSlice.ts';
import {
  createDoraControl,
  deleteDoraControl,
  listDoraControls,
  updateDoraControl,
} from '../../../api.ts';
import { DoraControl, doraControlToUpdate } from '../models/doraControl.ts';
import { selectCurrentOrganization } from '../../../store/organization/organizationSlice.ts';

export const ControlsPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const controls = useAppSelector(selectDoraControls);
  const currentOrganization = useAppSelector(selectCurrentOrganization);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [controlType, setControlType] = useState('');

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editControlType, setEditControlType] = useState('');

  const sortedControls = useMemo(
    () => [...controls].sort((a, b) => a.name.localeCompare(b.name)),
    [controls],
  );

  const createDisabled = !name.trim();

  useEffect(() => {
    const load = async () => {
      if (!currentOrganization) return;
      setLoading(true);
      setError(null);
      try {
        const items = await listDoraControls();
        dispatch(setDoraControls(items));
      } catch (err: any) {
        console.error('Failed to load controls', err);
        setError(err?.response?.data?.detail || 'Failed to load controls');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentOrganization, dispatch]);

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const created = await createDoraControl({
        name: name.trim(),
        description: description || null,
        control_type: controlType || null,
      });
      dispatch(addDoraControl(created));
      setName('');
      setDescription('');
      setControlType('');
      setSuccess('Control created');
    } catch (err: any) {
      console.error('Failed to create control', err);
      setError(err?.response?.data?.detail || 'Failed to create control');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (control: DoraControl) => {
    setEditingId(control.id ?? null);
    setEditName(control.name);
    setEditDescription(control.description || '');
    setEditControlType(control.control_type || '');
    setError(null);
    setSuccess(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditDescription('');
    setEditControlType('');
  };

  const handleUpdate = async (control: DoraControl) => {
    if (!control.id) return;
    if (!editName.trim()) {
      setError('Name is required');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = doraControlToUpdate({
        ...control,
        name: editName.trim(),
        description: editDescription || null,
        control_type: editControlType || null,
      });
      const updated = await updateDoraControl(control.id, payload);
      dispatch(updateDoraControlState(updated));
      cancelEdit();
      setSuccess('Control updated');
    } catch (err: any) {
      console.error('Failed to update control', err);
      setError(err?.response?.data?.detail || 'Failed to update control');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id?: number) => {
    if (!id) return;
    if (!window.confirm('Delete this control?')) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await deleteDoraControl(id);
      dispatch(removeDoraControl(id));
      setSuccess('Control deleted');
    } catch (err: any) {
      console.error('Failed to delete control', err);
      setError(err?.response?.data?.detail || 'Failed to delete control');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Add Control
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Type (e.g. technical, organizational)"
              value={controlType}
              onChange={(e) => setControlType(e.target.value)}
              fullWidth
            />
          </Stack>
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
          />
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={handleCreate} disabled={loading || createDisabled}>
              Create
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6">Controls</Typography>
            {loading && <CircularProgress size={20} />}
          </Stack>
          {sortedControls.length === 0 ? (
            <Alert severity="info">No controls yet. Add one above.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedControls.map(control => {
                    const isEditing = editingId === control.id;
                    return (
                      <TableRow key={control.id ?? control.name}>
                        <TableCell width="20%">
                          {isEditing ? (
                            <TextField value={editName} onChange={(e) => setEditName(e.target.value)} size="small" fullWidth />
                          ) : control.name}
                        </TableCell>
                        <TableCell width="20%">
                          {isEditing ? (
                            <TextField value={editControlType} onChange={(e) => setEditControlType(e.target.value)} size="small" fullWidth />
                          ) : control.control_type || '—'}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField value={editDescription} onChange={(e) => setEditDescription(e.target.value)} size="small" fullWidth />
                          ) : control.description || '—'}
                        </TableCell>
                        <TableCell align="right">
                          {isEditing ? (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <IconButton onClick={() => handleUpdate(control)} size="small" disabled={loading}>
                                <SaveIcon fontSize="small" />
                              </IconButton>
                              <IconButton onClick={cancelEdit} size="small" disabled={loading}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          ) : (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <IconButton onClick={() => startEdit(control)} size="small" disabled={loading}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton onClick={() => handleDelete(control.id)} size="small" disabled={loading}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};

export default ControlsPanel;
